import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Users } from 'lucide-react';
import {
  Button,
  Table,
  Modal,
  Pagination,
  SearchBar,
  Loading,
  Alert,
  Card,
} from '../../components/common';
import OrganizacionForm from '../../components/organizaciones/OrganizacionForm';
import OrganizacionDetail from '../../components/organizaciones/OrganizacionDetail';
import { organizacionesAPI } from '../../api';

const OrganizacionesPage = () => {
  // Estado
  const [organizaciones, setOrganizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    activo: '',
  });

  // Ordenamiento
  const [sortColumn, setSortColumn] = useState('fecha_creacion');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUsuariosModal, setShowUsuariosModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgEstadisticas, setOrgEstadisticas] = useState(null);
  const [orgUsuarios, setOrgUsuarios] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  // Cargar organizaciones
  useEffect(() => {
    loadOrganizaciones();
  }, [currentPage, itemsPerPage, searchTerm, filters, sortColumn, sortDirection]);

  const loadOrganizaciones = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      if (searchTerm) params.buscar = searchTerm;
      if (filters.activo !== '') params.activo = filters.activo === 'true';

      const response = await organizacionesAPI.getAll(params);
      const data = response.data;

      setOrganizaciones(data.items || []);
      setTotalItems(data.total || 0);
      setTotalPages(Math.ceil((data.total || 0) / itemsPerPage));
    } catch (err) {
      setError(err.message || 'Error al cargar organizaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadOrgEstadisticas = async (id) => {
    try {
      const response = await organizacionesAPI.getEstadisticas(id);
      setOrgEstadisticas(response.data);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  const loadOrgUsuarios = async (id) => {
    try {
      const response = await organizacionesAPI.getUsuarios(id);
      setOrgUsuarios(response.data);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  };

  // Handlers
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
    });
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleCreate = async (orgData) => {
    try {
      setFormLoading(true);
      setFormError(null);

      await organizacionesAPI.create(orgData);

      setSuccess('Organización creada exitosamente');
      setShowCreateModal(false);
      loadOrganizaciones();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error:', err);
      const detail = err.response?.data?.detail;
      let errorMsg = 'Error al crear organización';

      // Evitar crash por renderizar objetos: convertir todo a string
      if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg).join('. ');
      } else if (typeof detail === 'object') {
        errorMsg = JSON.stringify(detail); // Convierte el objeto problemático a texto
      } else if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (err.message) {
        errorMsg = err.message;
      }

      setFormError(errorMsg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (orgData) => {
    try {
      setFormLoading(true);
      setFormError(null);

      await organizacionesAPI.update(selectedOrg.id_organizacion, orgData);

      setSuccess('Organización actualizada exitosamente');
      setShowEditModal(false);
      setSelectedOrg(null);
      loadOrganizaciones();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error:', err);
      const detail = err.response?.data?.detail;
      let errorMsg = 'Error al actualizar organización';

      // Evitar crash por renderizar objetos: convertir todo a string
      if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg).join('. ');
      } else if (typeof detail === 'object') {
        errorMsg = JSON.stringify(detail);
      } else if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (err.message) {
        errorMsg = err.message;
      }

      setFormError(errorMsg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setFormLoading(true);
      setFormError(null);

      await organizacionesAPI.delete(selectedOrg.id_organizacion);

      setSuccess('Organización eliminada exitosamente');
      setShowDeleteModal(false);
      setSelectedOrg(null);
      loadOrganizaciones();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Error al eliminar organización');
    } finally {
      setFormLoading(false);
    }
  };

  const handleViewDetail = async (org) => {
    setSelectedOrg(org);
    setOrgEstadisticas(null);
    setShowDetailModal(true);
    await loadOrgEstadisticas(org.id_organizacion);
  };

  const handleViewUsuarios = async (org) => {
    setSelectedOrg(org);
    setOrgUsuarios([]);
    setShowUsuariosModal(true);
    await loadOrgUsuarios(org.id_organizacion);
  };

  // Definir columnas de la tabla
  const columns = [
    {
      key: 'nombre',
      label: 'Nombre',
      sortable: true,
      render: (value, org) => (
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-white">
              {value?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">ID: {org.id_organizacion}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'direccion',
      label: 'Dirección',
      render: (value) => (
        <span className="text-sm text-gray-600">{value || 'N/A'}</span>
      ),
    },
    {
      key: 'telefono',
      label: 'Teléfono',
      render: (value) => (
        <span className="text-sm text-gray-600">{value || 'N/A'}</span>
      ),
    },
    {
      key: 'email_contacto',
      label: 'Email',
      render: (value) => (
        <span className="text-sm text-gray-600">{value || 'N/A'}</span>
      ),
    },
    {
      key: 'activo',
      label: 'Estado',
      sortable: true,
      render: (value) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {value ? 'Activa' : 'Inactiva'}
        </span>
      ),
    },
    {
      key: 'acciones',
      label: 'Acciones',
      render: (_, org) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewDetail(org)}
            className="text-blue-600 hover:text-blue-900"
            title="Ver detalle"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleViewUsuarios(org)}
            className="text-purple-600 hover:text-purple-900"
            title="Ver usuarios"
          >
            <Users className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              setSelectedOrg(org);
              setShowEditModal(true);
            }}
            className="text-yellow-600 hover:text-yellow-900"
            title="Editar"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              setSelectedOrg(org);
              setShowDeleteModal(true);
            }}
            className="text-red-600 hover:text-red-900"
            title="Eliminar"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  // Configuración de filtros
  const filterConfig = [
    {
      key: 'activo',
      label: 'Estado',
      type: 'select',
      value: filters.activo,
      options: [
        { value: 'true', label: 'Activa' },
        { value: 'false', label: 'Inactiva' },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Organizaciones</h1>
          <p className="mt-2 text-gray-600">
            Gestiona las organizaciones del sistema
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nueva Organización
        </Button>
      </div>

      {/* Alertas */}
      {success && (
        <Alert type="success" message={success} onClose={() => setSuccess(null)} />
      )}
      {error && (
        <Alert type="error" message={typeof error === 'string' ? error : error.message || 'Error al crear organización'} onClose={() => setError(null)} />
      )}

      {/* Búsqueda y filtros */}
      <SearchBar
        placeholder="Buscar por nombre..."
        onSearch={handleSearch}
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      {/* Tabla */}
      <Table
        columns={columns}
        data={organizaciones}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        loading={loading}
        emptyMessage="No se encontraron organizaciones"
      />

      {/* Paginación */}
      {!loading && totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Modal Crear */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormError(null);
        }}
        title="Crear Nueva Organización"
        size="lg"
      >
        <OrganizacionForm
          onSubmit={handleCreate}
          onCancel={() => {
            setShowCreateModal(false);
            setFormError(null);
          }}
          loading={formLoading}
          error={formError}
        />
      </Modal>

      {/* Modal Editar */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedOrg(null);
          setFormError(null);
        }}
        title="Editar Organización"
        size="lg"
      >
        <OrganizacionForm
          organizacion={selectedOrg}
          onSubmit={handleEdit}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedOrg(null);
            setFormError(null);
          }}
          loading={formLoading}
          error={formError}
        />
      </Modal>

      {/* Modal Detalle */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedOrg(null);
          setOrgEstadisticas(null);
        }}
        title="Detalle de la Organización"
        size="lg"
        footer={
          <Button
            onClick={() => {
              setShowDetailModal(false);
              setSelectedOrg(null);
              setOrgEstadisticas(null);
            }}
          >
            Cerrar
          </Button>
        }
      >
        {selectedOrg && (
          <OrganizacionDetail
            organizacion={selectedOrg}
            estadisticas={orgEstadisticas}
          />
        )}
      </Modal>

      {/* Modal Usuarios */}
      <Modal
        isOpen={showUsuariosModal}
        onClose={() => {
          setShowUsuariosModal(false);
          setSelectedOrg(null);
          setOrgUsuarios([]);
        }}
        title={`Usuarios de ${selectedOrg?.nombre || 'Organización'}`}
        size="lg"
        footer={
          <Button
            onClick={() => {
              setShowUsuariosModal(false);
              setSelectedOrg(null);
              setOrgUsuarios([]);
            }}
          >
            Cerrar
          </Button>
        }
      >
        {orgUsuarios.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>No hay usuarios en esta organización</p>
          </div>
        ) : (
          <div className="space-y-2">
            {orgUsuarios.map((usuario) => (
              <Card key={usuario.id_usuario} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center mr-3">
                      <span className="text-white font-semibold">
                        {usuario.nombre_completo?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {usuario.nombre_completo}
                      </p>
                      <p className="text-sm text-gray-500">{usuario.email}</p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      usuario.rol === 'superadmin'
                        ? 'bg-purple-100 text-purple-800'
                        : usuario.rol === 'admin_org'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {usuario.rol}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Modal>

      {/* Modal Eliminar */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedOrg(null);
          setFormError(null);
        }}
        title="Confirmar Eliminación"
        size="sm"
      >
        <div className="space-y-4">
          {formError && <Alert type="error" message={formError} />}

          <p className="text-gray-700">
            ¿Estás seguro de que deseas eliminar la organización{' '}
            <strong>{selectedOrg?.nombre}</strong>?
          </p>
          <p className="text-sm text-gray-600">
            Esta acción desactivará la organización pero mantendrá su registro en el
            sistema.
          </p>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedOrg(null);
                setFormError(null);
              }}
              disabled={formLoading}
            >
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={formLoading}>
              Eliminar Organización
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizacionesPage;
